import type { UseFormReturn } from 'react-hook-form';
import {
  Checkbox,
  Disclosure,
  DisclosureButton,
  Labeled,
  OptionCard,
  Radio,
  RadioGroup,
  TextInputField,
} from '@sixui/ui';
import { densityTokens } from '@sixui/ui/themes/base/density.stylex';
import { scaleTokens } from '@sixui/ui/themes/base/scale.stylex';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { ISendPushNotificationFormValues } from './SendPushNotificationForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';

export type ISendPushNotificationFormAdvancedOptionsSectionProps =
  IContainerProps & {
    form: UseFormReturn<ISendPushNotificationFormValues>;
  };

const MIN_DENSITY = -1;
const MAX_DENSITY = 0;
const DENSITY = `${densityTokens.interval} * clamp(${MIN_DENSITY}, ${densityTokens.density}, ${MAX_DENSITY}) * ${scaleTokens.scale}`;

const styles = stylex.create({
  linkTypeOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridAutoRows: '1fr',
    columnGap: spacingTokens.padding$2,
    rowGap: `calc(${spacingTokens.padding$2} + ${DENSITY})`,
  },
});

export const SendPushNotificationFormAdvancedOptionsSection: React.FC<
  ISendPushNotificationFormAdvancedOptionsSectionProps
> = (props) => {
  const { sx, form } = props;
  const { t } = useTranslation();

  const target = form.watch('target');

  return (
    <Disclosure
      sx={sx}
      trigger={
        <DisclosureButton>{t('common.advanced-options')}</DisclosureButton>
      }
    >
      <FormSection sub>
        <Labeled
          label={t('SendPushNotificationFormMainSection.fields.target.label')}
        >
          <FormField
            name="target"
            control={form.control}
            as={RadioGroup}
            sx={styles.linkTypeOptions}
          >
            {['feed', 'article' /*, 'userArticle', 'chatRoom', 'url' */].map(
              (target, targetIndex) => (
                <OptionCard
                  key={targetIndex}
                  as={Radio}
                  label={t(
                    `SendPushNotificationFormMainSection.targets.${target}.label`,
                  )}
                  value={target}
                />
              ),
            )}
          </FormField>
        </Labeled>

        {target && target !== 'feed' && (
          <>
            <Labeled
              label={t(
                `SendPushNotificationFormMainSection.targets.${target}.valueLabel`,
              )}
              required
            >
              <FormField
                name="targetValue"
                control={form.control}
                as={TextInputField}
                forwardError
              />
            </Labeled>

            {target === 'url' && (
              <Labeled
                label={t(
                  'SendPushNotificationFormMainSection.fields.hideNavbar.label',
                )}
                labelPosition="right"
              >
                <FormField
                  name="hideNavbar"
                  control={form.control}
                  as={Checkbox}
                />
              </Labeled>
            )}
          </>
        )}
      </FormSection>
    </Disclosure>
  );
};
